import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as SolidIcons from '@heroicons/react/24/solid';

// --- Reusable Form Components ---
const FormInput = ({ id, name, label, type, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type={type} id={id} name={name} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);

const SelectInput = ({ id, name, label, value, onChange, options, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <select id={id} name={name} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white">
            <option value="">Select an icon...</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const Checkbox = ({ id, name, label, checked, onChange }) => (
    <div className="flex items-center">
        <input type="checkbox" id={id} name={name} checked={checked} onChange={onChange} className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500" />
        <label htmlFor={id} className="ml-2 block text-sm text-gray-900 dark:text-gray-300">{label}</label>
    </div>
);

// Get a list of all solid Heroicon names for the dropdown
const heroIconNames = Object.keys(SolidIcons).filter(key => key.endsWith('Icon'));

export default function ManageStatsPage() {
    const { token } = useAuth();
    const [stats, setStats] = useState([]);
    const [donationGoal, setDonationGoal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [editingStat, setEditingStat] = useState(null);
    const [activeTab, setActiveTab] = useState('stats'); // 'stats' or 'donation-goal'

    const [statFormData, setStatFormData] = useState({ 
        title: '', 
        stat_value: 0, 
        icon_name: '', 
        is_visible: true, 
        display_order: 0 
    });

    const [goalFormData, setGoalFormData] = useState({
        title: 'Assistive Items Gifted',
        current_amount: 0,
        target_amount: 5000,
        target_year: 2026,
        description: 'Join our community in providing assistive technology to those in need',
        is_active: true
    });

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            // Fetch stats
            const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/stats`, { 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!statsResponse.ok) throw new Error('Could not fetch stats.');
            const statsData = await statsResponse.json();
            setStats(statsData);

            // Fetch donation goal
           const goalResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/donation-goal`, {
    headers: { 'Authorization': `Bearer ${token}` }
});

            if (goalResponse.ok) {
                const goalData = await goalResponse.json();
                setDonationGoal(goalData);
                if (goalData) {
                    setGoalFormData(goalData);
                }
            }
        } catch (err) { 
            setError(err.message); 
        } finally { 
            setLoading(false); 
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (editingStat) {
            setStatFormData(editingStat);
        } else {
            setStatFormData({ title: '', stat_value: 0, icon_name: '', is_visible: true, display_order: 0 });
        }
    }, [editingStat]);

    const handleStatChange = (e) => {
        const { name, value, type, checked } = e.target;
        setStatFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleGoalChange = (e) => {
        const { name, value, type, checked } = e.target;
        setGoalFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : 
                    type === 'number' ? parseInt(value) : value 
        }));
    };

    const handleStatSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('');
        setError('');

        const url = editingStat 
            ? `${process.env.REACT_APP_API_URL}/admin/stats/${editingStat.id}`
            : `${process.env.REACT_APP_API_URL}/admin/stats`;

        const method = editingStat ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(statFormData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);

            setMessage(editingStat ? 'Stat updated successfully!' : 'Stat created successfully!');
            setEditingStat(null);
            setStatFormData({ title: '', stat_value: 0, icon_name: '', is_visible: true, display_order: 0 });
            fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

   const handleGoalSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setError('');

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/donation-goal`, {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${token}` 
            },
            body: JSON.stringify(goalFormData)
        });
        
        // Get the response text first to see what's actually being returned
        const responseText = await response.text();
        console.log('Raw response:', responseText);
        
        let data;
        try {
            data = JSON.parse(responseText);
        } catch (parseError) {
            console.error('Failed to parse JSON:', parseError);
            throw new Error(`Server returned non-JSON: ${responseText.substring(0, 100)}`);
        }
        
        if (!response.ok) {
            console.error('Server error details:', data);
            throw new Error(data.message || `Server error: ${response.status}`);
        }

        setMessage('Donation goal updated successfully!');
        fetchData();
    } catch (err) {
        console.error('Submit error:', err);
        setError(err.message);
    } finally {
        setIsSubmitting(false);
    }
};
    
    const handleDelete = async (statId) => {
        if (!window.confirm('Are you sure you want to delete this stat?')) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/stats/${statId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setMessage(data.message);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <p className="p-8 text-center">Loading Site Stats...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage Site Statistics & Goals</h1>
            {error && <p className="p-4 text-center font-semibold text-red-500 bg-red-100 dark:bg-red-900/50 rounded-lg">{error}</p>}
            {message && <p className="p-4 text-center font-semibold text-green-500 bg-green-100 dark:bg-green-900/50 rounded-lg">{message}</p>}
            
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'stats'
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        Statistics
                    </button>
                    <button
                        onClick={() => setActiveTab('donation-goal')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'donation-goal'
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        Donation Goal
                    </button>
                </nav>
            </div>

            {activeTab === 'stats' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                            <form onSubmit={handleStatSubmit} className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                                    {editingStat ? 'Edit Stat' : 'Add New Stat'}
                                </h2>
                                <FormInput id="title" name="title" label="Title" type="text" value={statFormData.title} onChange={handleStatChange} />
                                <FormInput id="stat_value" name="stat_value" label="Value (Number)" type="number" value={statFormData.stat_value} onChange={handleStatChange} />
                                <SelectInput id="icon_name" name="icon_name" label="Icon" value={statFormData.icon_name} onChange={handleStatChange} options={heroIconNames} />
                                <FormInput id="display_order" name="display_order" label="Display Order" type="number" value={statFormData.display_order} onChange={handleStatChange} />
                                <Checkbox id="is_visible" name="is_visible" label="Visible on Homepage" checked={statFormData.is_visible} onChange={handleStatChange} />

                                <div className="flex gap-4">
                                    {editingStat && (
                                        <button 
                                            type="button" 
                                            onClick={() => setEditingStat(null)} 
                                            className="w-full py-3 bg-gray-500 text-white font-bold rounded-lg hover:bg-gray-600"
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                    <button 
                                        type="submit" 
                                        disabled={isSubmitting} 
                                        className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Saving...' : (editingStat ? 'Save Changes' : 'Create Stat')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Current Stats</h2>
                            <div className="space-y-2">
                                {stats.map(stat => (
                                    <div key={stat.id} className="flex justify-between items-center p-3 border-b dark:border-gray-600 last:border-b-0">
                                        <div className="flex items-center gap-4">
                                            <div className="text-orange-500">
                                                {SolidIcons[stat.icon_name] ? React.createElement(SolidIcons[stat.icon_name], { className: 'w-8 h-8' }) : <SolidIcons.QuestionMarkCircleIcon className="w-8 h-8" />}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800 dark:text-white">{stat.title}</p>
                                                <p className="text-sm text-gray-500">Value: {stat.stat_value} | Order: {stat.display_order} | Visible: {stat.is_visible ? 'Yes' : 'No'}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => setEditingStat(stat)} className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full" title="Edit">✏️</button>
                                            <button onClick={() => handleDelete(stat.id)} className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full" title="Delete">🗑️</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                            <form onSubmit={handleGoalSubmit} className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                                    Donation Goal
                                </h2>
                                <FormInput id="goal_title" name="title" label="Goal Title" type="text" value={goalFormData.title} onChange={handleGoalChange} />
                                <FormInput id="current_amount" name="current_amount" label="Current Amount" type="number" value={goalFormData.current_amount} onChange={handleGoalChange} />
                                <FormInput id="target_amount" name="target_amount" label="Target Amount" type="number" value={goalFormData.target_amount} onChange={handleGoalChange} />
                                <FormInput id="target_year" name="target_year" label="Target Year" type="number" value={goalFormData.target_year} onChange={handleGoalChange} />
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                    <textarea 
                                        id="description" 
                                        name="description" 
                                        value={goalFormData.description} 
                                        onChange={handleGoalChange} 
                                        rows="3"
                                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                                <Checkbox id="is_active" name="is_active" label="Active on Website" checked={goalFormData.is_active} onChange={handleGoalChange} />

                                <button 
                                    type="submit" 
                                    disabled={isSubmitting} 
                                    className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Saving...' : 'Save Goal'}
                                </button>
                            </form>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Current Donation Goal Preview</h2>
                            {donationGoal && donationGoal.is_active ? (
                                <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl shadow-xl p-8 border border-orange-100 dark:border-gray-600">
                                    <div className="text-center mb-8">
                                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                            Together, We're Making a Difference
                                        </h3>
                                        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                                            This isn't just our goal—it's <span className="font-semibold text-orange-600 dark:text-orange-400">our community's achievement</span>. 
                                            Every donation brings us closer to changing lives through assistive technology.
                                        </p>
                                    </div>

                                    <div className="max-w-4xl mx-auto">
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="text-xl font-bold text-gray-800 dark:text-white">
                                                Community Goal: {donationGoal.title} by {donationGoal.target_year}
                                            </h4>
                                            <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                                                {donationGoal.current_amount.toLocaleString()} / {donationGoal.target_amount.toLocaleString()}
                                            </span>
                                        </div>

                                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-6 mb-4 overflow-hidden">
                                            <div 
                                                className="h-6 rounded-full transition-all duration-1000 ease-out relative"
                                                style={{ 
                                                    width: `${Math.min((donationGoal.current_amount / donationGoal.target_amount) * 100, 100)}%`,
                                                    backgroundColor: '#e46809'
                                                }}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20"></div>
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <p className="text-gray-600 dark:text-gray-300">
                                                Progress: {Math.round((donationGoal.current_amount / donationGoal.target_amount) * 100)}% • 
                                                {' '}{donationGoal.target_amount - donationGoal.current_amount} items needed
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                                    No active donation goal set. Create one to display on the website.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}